/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.nativeSelector = document.getElementById(`variants-${this.section.dataset.section}`);
      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.updateAddToCartButton();
      this.updatePreorderDisclaimer();
      this.syncStickyAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));

      this.setAttribute('loaded', '');
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      automaticallySelectFirstAvailableOption(evt);
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;
      this.updateAvailability();
      this.updateUrl(evt);
      this.updateVariantInput();
      this.updatePrice();
      this.updateStickyCartImage();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      this.updateAddToCartButton();
      VariantPicker.updateLabelText(evt);
      this.updatePreorderDisclaimer();
      this.syncStickyAddToCartButton();

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          allVariants: this.data.variants,
          selectedOptions: this.selectedOptions
        }

      }));
      //initAltAddToCart();
      changeCTAVariantLabel(this);
    }

    /**
     * Updates the "Add to Cart" button label and status.
     */
    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      if (!this.variant) return;

      const hasPreorderDate = VariantPicker.getPreorderDate(this.data, this.variant);
      const canPurchase = this.variant.available || hasPreorderDate;
      const unavailableStr = theme.strings.noStock;
      this.variantPrice = '$' + (this.variant.price / 100).toFixed(2);
      this.addBtnText = this.addBtn.querySelector('.add-to-cart-text');
      this.addBtnPrice = this.addBtn.querySelector('.add-to-cart-price');

      this.addBtn.disabled = !canPurchase;

      if (hasPreorderDate) {
        this.addBtnText.innerHTML = theme.strings.preorder || 'Pre-order now';
      } else {
        this.addBtnText.innerHTML = this.variant.available ? this.addBtn.dataset.addToCartText : unavailableStr;
      }

      this.addBtnPrice.innerHTML = this.variantPrice;
      this.addBtnPrice.setAttribute('data-variant-id', this.variant.id);
    }

    /**
     * Mirrors the main add-to-cart button state in the sticky cart CTA.
     */
    syncStickyAddToCartButton() {
      const mainBtn = this.productForm?.querySelector('[name="add"]');
      const stickyBtn = document.querySelector('.js-sticky-add-to-cart');
      if (!mainBtn || !stickyBtn) return;

      stickyBtn.disabled = mainBtn.disabled;

      const mainText = mainBtn.querySelector('.add-to-cart-text');
      const stickyText = stickyBtn.querySelector('.add-to-cart-text');
      if (mainText && stickyText) {
        stickyText.innerHTML = mainText.innerHTML;
      }
    }

    static getPreorderDate(data, variant) {
      if (!variant || !data?.formatted) return false;
      return data.formatted[variant.id]?.preOrderDate || false;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;

      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);


      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      // selectedOption is the selected size option
      let selectedOption = document.querySelector('input:checked[data-option-type="Size"]') ? document.querySelector('input:checked[data-option-type="Size"]') : document.querySelector('input:checked[data-option-type="Shape"]');

      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMode } = this.dataset; // 'down' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMode === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let optionType = option.dataset.optionType;
            let variantsExist = false;
            let variantsAvailable = false;
            
            this.data.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                if(optionType === "Color" && selectedOption !== null){
                
                  let colorOptions = selectedOption.dataset.variant;
                  if(colorOptions.includes(v.option1)){
                    variantsExist = true;
                  } else {
                    variantsExist = false;
                  }
                } else {
                  variantsExist = true;
                }
                if (v.available) {
                  variantsAvailable = true;
                }
              }
              /*if(optionType === "Color"){
                let selectedOption = document.querySelector('.js-option[data-option-type="Size"][checked]');
                let colorOptions = selectedOption.dataset.variant;
                if(colorOptions.includes(v.option1)){
                  console.log('colorOptions.includes(v.id)',colorOptions.includes(v.option1));
                  variantsExist = true;
                } else {
                  variantsExist = false;
                }
              }*/
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the colour option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (!selector || selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      const optionInput = evt.target.classList.contains('js-option')
        ? evt.target
        : selector.querySelector('input:checked');
      const valueEl = optionInput?.nextElementSibling?.querySelector('.js-value');
      if (valueEl) {
        colorText.textContent = valueEl.textContent;
      }
    }

    /**
     * Updates pre-order disclaimer, line item property, and add-to-cart state from the resolved variant.
     */
    updatePreorderDisclaimer() {
      if (this.selectedOptions.indexOf(null) > -1) return;

      const preorderText = this.section.querySelector('.js-preorder-text') ? this.section.querySelector('.js-preorder-text') : null;
      const variantInfoText = this.section.querySelector('.js-info-text');
      const addToCartBtn = this.section.querySelector('.quantity-submit-row__submit .btn');
      const addToCartBtnText = this.section.querySelector('.add-to-cart-text');
      const preOrderProperty = this.section.querySelector('.js-pre-order-property');
      const preOrderWrapper = this.section.querySelector('.pre-order-disclaimer');

      if (!this.variant) return;

      const formatted = this.data.formatted[this.variant.id] || {};
      const hasPreorderDate = formatted.preOrderDate || false;
      const hasVariantInfo = formatted.variantInfo || false;
      const isSoldOut = !this.variant.available;
      const canPurchase = this.variant.available || hasPreorderDate;

      console.log('FORMATTED', formatted);

      variantInfoText.textContent = hasVariantInfo || '';

      if (preorderText) {
        preorderText.textContent = hasPreorderDate || '';
      }

      if (preOrderProperty) {
        if (hasPreorderDate) {
          preOrderProperty.value = `Ships ${hasPreorderDate}`;
          preOrderProperty.checked = true;
        } else {
          preOrderProperty.value = '';
          preOrderProperty.checked = false;
        }
      }

      if (addToCartBtnText) {
        if (hasPreorderDate) {
          addToCartBtnText.textContent = theme.strings.preorder || 'Pre-order now';
        } else if (isSoldOut) {
          addToCartBtnText.textContent = theme.strings.noStock || 'Sold Out';
        } else if (this.addBtn) {
          addToCartBtnText.textContent = this.addBtn.dataset.addToCartText || theme.strings.addToCart || 'Add to Cart';
        }
      } 

      if (addToCartBtn) {
        if (!canPurchase) {
          addToCartBtn.disabled = true;
        } else if (isSoldOut && hasPreorderDate) {
          addToCartBtn.disabled = true;
        } else {
          addToCartBtn.disabled = false;
        }
      }

      if (preOrderWrapper) {
        if (hasPreorderDate) {
          preOrderWrapper.classList.remove('hidden');
          preOrderWrapper.classList.toggle('sold-out', isSoldOut);
          if (!isSoldOut && typeof preOrderDisclaimer === 'function') {
            preOrderDisclaimer('approved');
          }
        } else {
          preOrderWrapper.classList.add('hidden');
          preOrderWrapper.classList.remove('sold-out');
        }
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-price .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price) variant = this.data.variants[i];
        }
      }

      if (variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const saleTagEl = this.price.querySelector('.price-label--sale');
        const unitPriceEl = this.price.querySelector('.unit-price');
        const stickyPrice = document.querySelector('.sticky-cart-cta--price .price');
        const stickyPriceCurrentEl = stickyPrice?.querySelector('.price__current');
        const stickyPriceWasEl = stickyPrice?.querySelector('.price__was');


        //let hasPriceWasMetafield = priceWasEl.classList.contains('no-update') ? false : priceWasEl.dataset.comparePrice;
        let hasPriceWasMetafield = priceWasEl.dataset.comparePrice ? priceWasEl.dataset.comparePrice : false;

        if (hasPriceWasMetafield) {
          hasPriceWasMetafield = JSON.parse(hasPriceWasMetafield);
        }

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = this.data.formatted[variant.id].price;
        priceCurrentEl.setAttribute('data-variant-id', variant.id);

        if(stickyPriceCurrentEl) {
          stickyPriceCurrentEl.innerHTML = this.data.formatted[variant.id].price;
        }

        if (!hasPriceWasMetafield) {
          if(priceWasEl) {
             priceWasEl.innerHTML = this.data.formatted[variant.id].compareAtPrice || '';
             if(saleTagEl) {
              saleTagEl.classList.toggle('hidden', !variant.compare_at_price);
             }
          }
          if(stickyPriceWasEl) {
            stickyPriceWasEl.innerHTML = this.data.formatted[variant.id].compareAtPrice || '';
          }
        } else {
          if(this.data.formatted[variant.id].price == hasPriceWasMetafield && this.data.formatted[variant.id].compareAtPrice != undefined) {
            priceWasEl.classList.add('hidden');
            if(saleTagEl) {
              saleTagEl.classList.add('hidden');
            }
            if(stickyPriceWasEl) {
              stickyPriceWasEl.classList.add('hidden');
            }
          } else if (this.data.formatted[variant.id].compareAtPrice == undefined) {
            priceWasEl.classList.add('hidden');
            if(saleTagEl) {
              saleTagEl.classList.add('hidden');
            }
            if(stickyPriceWasEl) {
              stickyPriceWasEl.classList.add('hidden');
            }
          } else {
            let newComparePrice = false; 
            hasPriceWasMetafield.variants_price.forEach((variant_price) => {
              if(variant_price.id == variant.id) {
                newComparePrice = variant_price.price;
              }
            });
            if(!newComparePrice) {
              newComparePrice = hasPriceWasMetafield.product_price
            }

            priceWasEl.innerHTML = newComparePrice;
            priceWasEl.setAttribute('data-variant-id', variant.id);
            priceWasEl.classList.remove('hidden');
            if(saleTagEl) {
              saleTagEl.classList.remove('hidden');
            }
            if(stickyPriceWasEl) {
              stickyPriceWasEl.classList.remove('hidden');
              stickyPriceWasEl.innerHTML = newComparePrice;
            }
          }
        }

        // Update unit price, if specified.
        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        this.price.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        this.price.classList.toggle('price--sold-out', !variant.available && !this.preSelection);
        if(stickyPrice) {
          stickyPrice.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
          stickyPrice.classList.toggle('price--sold-out', !variant.available && !this.preSelection);
        }
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the sticky cart variant image.
     */
    updateStickyCartImage() {
      const stickyImageWrapper = document.querySelector('.sticky-cart-cta--image');
      const stickyImage = stickyImageWrapper?.querySelector('.sticky-cart-cta--image-img');
      if (!stickyImage) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price) variant = this.data.variants[i];
        }
      }

      let imageSrc = variant?.featured_media?.preview_image?.src || variant?.featured_image?.src;
      if (!imageSrc && stickyImageWrapper.dataset.fallbackSrc) {
        imageSrc = stickyImageWrapper.dataset.fallbackSrc;
      }
      if (!imageSrc) return;

      const sizedSrc = VariantPicker.getSizedImageUrl(imageSrc, 120);
      stickyImage.src = sizedSrc;
      stickyImage.srcset = `${VariantPicker.getSizedImageUrl(imageSrc, 80)} 80w, ${sizedSrc} 120w, ${VariantPicker.getSizedImageUrl(imageSrc, 160)} 160w`;

      const alt = variant?.featured_media?.preview_image?.alt || variant?.featured_image?.alt;
      if (alt) stickyImage.alt = alt;
    }

    static getSizedImageUrl(src, width) {
      const url = src.startsWith('//') ? `https:${src}` : src;
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}width=${width}`;
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcode = this.barcode || this.section.querySelector('.product-barcode__value');
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        input.value = this.variant ? this.variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];
      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');

          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      return this.data.variants.find(
        (v) => v.options.every((val, index) => val === this.selectedOptions[index])
      );
    }

  }

  customElements.define('variant-picker', VariantPicker);
}


function automaticallySelectFirstAvailableOption(evt) {
  if (evt.target.dataset.optionType !== 'Color') {
    return;
  }

  const variantPicker = evt.target.closest('variant-picker');
  if (!variantPicker) return;

  const color = evt.target.value;
  const optionSelectors = variantPicker.querySelectorAll('.option-selector');
  const optionSelector2 = optionSelectors[1];
  if (!optionSelector2) return;

  const option2 = optionSelector2.querySelectorAll('input');
  const checkedOption = optionSelector2.querySelector('input:checked');
  if (!checkedOption?.dataset.variant) return;

  if (!checkedOption.dataset.variant.includes(color)) {
    option2.forEach((option) => {
      if (option.dataset.variant.includes(color)) {
        option.checked = true;
      } else {
        option.checked = false;
      }
    });
  }
}
